import React from 'react';
import { motion } from 'framer-motion';
import './ScatterText.css';
import './ScatterText-responsive.css';

interface ScatterTextProps {
  text: string;
  className?: string;
}

const ScatterText: React.FC<ScatterTextProps> = ({ text, className = '' }) => {
  // Split text into words to preserve word boundaries
  const words = text.split(' ');
  
  // Animation variants for the container
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: (i = 1) => ({
      opacity: 1,
      transition: { 
        staggerChildren: 0.03,
        delayChildren: i * 0.04 
      }
    })
  };
  
  // Animation variants for each character
  const characterVariants = {
    hidden: {
      opacity: 0,
      y: 20,
      rotate: Math.random() * 30 - 15,
      scale: 0
    },
    visible: {
      opacity: 1,
      y: 0,
      rotate: 0,
      scale: 1,
      transition: {
        type: 'spring',
        damping: 10,
        stiffness: 100
      }
    },
    hover: {
      y: -5,
      scale: 1.2,
      color: 'var(--accent-color)',
      transition: {
        type: 'spring',
        damping: 10,
        stiffness: 300
      }
    }
  };
  
  // For mobile view, detect if it's a name (contains "Muhammad")
  const isName = text.includes("Muhammad");

  return (
    <motion.div
      className={`scatter-text-container ${className} ${isName ? 'name-text' : ''}`}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {words.map((word, wordIndex) => (
        <React.Fragment key={`word-${wordIndex}`}>
          {/* Render each word as a unit to prevent breaking */}
          <span className="scatter-word">
            {Array.from(word).map((char: string, charIndex: number) => (
              <motion.span
                key={`${char}-${wordIndex}-${charIndex}`}
                variants={characterVariants}
                className="scatter-text-char"
                whileHover="hover"
              >
                {char}
              </motion.span>
            ))}
          </span>
          {/* Add space between words, but not after the last word */}
          {wordIndex < words.length - 1 && (
            <span className="scatter-space">&nbsp;</span>
          )}
        </React.Fragment>
      ))}
    </motion.div>
  );
};

export default ScatterText;
